import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/site';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = useMemo(() => getArticleById(Number(id)), [id]);
  const relatedArticles = article ? getRelatedArticles(article) : [];

  if (!article) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-foreground-300"></i>
          <h2 className="mt-4 text-xl font-heading font-bold text-foreground-800">文章不存在</h2>
          <p className="mt-2 text-foreground-500">该爆料内容可能已被删除或链接无效</p>
          <Link to="/" className="mt-6 inline-block px-6 py-2 bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap font-label text-sm">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const pageTitle = `${article.title} - ${siteInfo.name}`;
  const pageDescription = article.excerpt;
  const pageKeywords = `${article.tags.join(',')},${article.categoryName},黑料,爆料,吃瓜,${siteInfo.name}`;
  const canonicalUrl = `${siteInfo.url}/article/${article.id}`;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
      url: siteInfo.url,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    about: article.tags.map((tag) => ({ '@type': 'Thing', name: tag })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@id': `${siteInfo.url}/`, name: '首页' } },
      { '@type': 'ListItem', position: 2, item: { '@id': `${siteInfo.url}/category/${article.category}`, name: article.categoryName } },
      { '@type': 'ListItem', position: 3, item: { '@id': canonicalUrl, name: article.title } },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      <div className="pt-20 bg-background-50 min-h-screen">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: article.categoryName, to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />
          <div className="flex flex-col lg:flex-row gap-8 pb-14">
            <article className="lg:w-[70%]">
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-label rounded-full whitespace-nowrap">
                    {article.categoryName}
                  </span>
                  {article.isHot && (
                    <span className="flex items-center gap-0.5 text-accent-500 text-xs font-label whitespace-nowrap">
                      <i className="ri-fire-fill"></i> 热门爆料
                    </span>
                  )}
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500 font-label flex-wrap">
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-user-line"></i> {article.author}
                  </span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-time-line"></i> {new Date(article.publishedAt).toLocaleString('zh-CN')}
                  </span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-eye-line"></i> {(article.views / 10000).toFixed(0)}万阅读
                  </span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-chat-3-line"></i> {article.comments}评论
                  </span>
                </div>
              </div>

              <div className="mb-8 rounded-lg overflow-hidden">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  title={`${article.title} - 爆料网`}
                  className="w-full h-auto max-h-[450px] object-cover object-top"
                />
              </div>

              <div
                className="prose-content text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-8 pt-6 border-t border-background-200/70">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-foreground-500 font-label whitespace-nowrap">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-secondary-100 text-secondary-900 text-xs font-label rounded-full whitespace-nowrap"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between py-4 border-t border-background-200/70">
                <button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap font-label"
                >
                  <i className="ri-arrow-up-line"></i> 返回顶部
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-400 whitespace-nowrap">分享到：</span>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-500 hover:text-background-50 transition-all cursor-pointer" aria-label="分享到微博">
                    <i className="ri-weibo-line text-sm"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-accent-500 hover:text-background-50 transition-all cursor-pointer" aria-label="分享到微信">
                    <i className="ri-wechat-line text-sm"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-500 hover:text-background-50 transition-all cursor-pointer" aria-label="复制链接">
                    <i className="ri-link text-sm"></i>
                  </button>
                </div>
              </div>
            </article>

            <aside className="lg:w-[30%]">
              <div className="sticky top-24">
                <div className="bg-background-100 rounded-lg p-5 border border-background-200/70 mb-6">
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-links-line text-primary-500"></i> 相关爆料
                  </h3>
                  <div className="flex flex-col gap-4">
                    {relatedArticles.map((rel) => (
                      <Link
                        key={rel.id}
                        to={`/article/${rel.id}`}
                        className="group flex gap-3 cursor-pointer"
                      >
                        <div className="w-20 h-14 flex-shrink-0 rounded overflow-hidden">
                          <img
                            src={rel.coverImage}
                            alt={rel.title}
                            className="w-full h-full object-cover object-top"
                            loading="lazy"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-label font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                            {rel.title}
                          </p>
                          <span className="text-xs text-foreground-400 mt-1 inline-block whitespace-nowrap">
                            {(rel.views / 10000).toFixed(0)}万阅读
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-fire-fill text-accent-500"></i> 今日热门
                  </h3>
                  <div className="flex flex-col gap-3">
                    {getArticleById(1) && (
                      <Link to="/article/1" className="text-xs font-label text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer line-clamp-2">
                        <span className="text-accent-500 font-bold mr-1">TOP1</span>
                        {getArticleById(1)?.title}
                      </Link>
                    )}
                    {getArticleById(2) && (
                      <Link to="/article/2" className="text-xs font-label text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer line-clamp-2">
                        <span className="text-accent-500 font-bold mr-1">TOP2</span>
                        {getArticleById(2)?.title}
                      </Link>
                    )}
                    {getArticleById(3) && (
                      <Link to="/article/3" className="text-xs font-label text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer line-clamp-2">
                        <span className="text-foreground-500 font-bold mr-1">TOP3</span>
                        {getArticleById(3)?.title}
                      </Link>
                    )}
                    {getArticleById(5) && (
                      <Link to="/article/5" className="text-xs font-label text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer line-clamp-2">
                        <span className="text-foreground-500 font-bold mr-1">TOP4</span>
                        {getArticleById(5)?.title}
                      </Link>
                    )}
                    {getArticleById(10) && (
                      <Link to="/article/10" className="text-xs font-label text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer line-clamp-2">
                        <span className="text-foreground-500 font-bold mr-1">TOP5</span>
                        {getArticleById(10)?.title}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
}